import { ref } from 'vue';
import { ElMessage } from 'element-plus';
import type { Ref } from 'vue';
import { ajax } from '@/api';
import type { LevelItem, LevelSort, LevelTab, SearchField } from './types';

type RouterLike = {
  push: (to: string | { path: string; query?: Record<string, unknown> }) => void | Promise<void>;
};

export const useLevelExplorerCollections = (options: {
  tab: Ref<LevelTab>;
  archiveLevels: Ref<LevelItem[]>;
  mineLevels: Ref<LevelItem[]>;
  favoriteLevels: Ref<LevelItem[]>;
  activeLevel: Ref<LevelItem | null>;
  searchField: Ref<SearchField>;
  keyword: Ref<string>;
  statusFilter: Ref<number | ''>;
  levelSort: Ref<LevelSort>;
  patrolExpireDays: Ref<number>;
  editForm: Partial<LevelItem>;
  requireLogin: () => boolean;
  leaveVisitStatsRoute: () => void;
  router: RouterLike;
}) => {
  const {
    tab,
    archiveLevels,
    mineLevels,
    favoriteLevels,
    activeLevel,
    searchField,
    keyword,
    statusFilter,
    levelSort,
    patrolExpireDays,
    editForm,
    requireLogin,
    leaveVisitStatsRoute,
    router
  } = options;
  const archiveLoading = ref(false);
  const archiveLoadingMore = ref(false);
  const archiveHasNext = ref(true);
  const archiveReachedEnd = ref(false);
  const archiveNextLastId = ref<number | null>(null);
  const archiveTotal = ref(0);
  let archiveRequestToken = 0;
  const archivePageSize = 20;

  const getBlockedLevelReason = (level: Partial<LevelItem>) => {
    if (Number(level.auditStatus) === 1) return '该层级资料正在管理员审核中，暂时不能查看。';
    if (Number(level.auditStatus) === 3) {
      return `该层级资料审核未通过，暂时不能查看。${level.rejectReason ? `原因：${level.rejectReason}` : ''}`;
    }
    if (Number(level.status) === -1) return '该层级已被删除，不能查看。';
    return '当前层级暂时不能查看。';
  };

  const loadArchive = (append = false) => {
    if (append && (archiveLoading.value || archiveLoadingMore.value || !archiveHasNext.value)) return;
    if (append && !archiveNextLastId.value) return;
    const requestToken = ++archiveRequestToken;
    if (append) {
      archiveLoadingMore.value = true;
    } else {
      archiveLoading.value = true;
      archiveHasNext.value = true;
      archiveReachedEnd.value = false;
      archiveNextLastId.value = null;
    }
    const trimmedKeyword = keyword.value.trim();
    ajax({
      url: 'level/list',
      method: 'POST',
      data: {
        paged: true,
        page: 1,
        size: archivePageSize,
        lastId: append ? archiveNextLastId.value : undefined,
        levelSort: levelSort.value,
        searchField: trimmedKeyword ? searchField.value : undefined,
        keyword: trimmedKeyword || undefined,
        status: statusFilter.value === '' ? undefined : statusFilter.value,
        auditStatuses: [0, 1, 2]
      },
      isLoading: false,
      success: (res: any) => {
        if (requestToken !== archiveRequestToken) return;
        archiveLoading.value = false;
        archiveLoadingMore.value = false;
        if (res.code === 0) {
          const list = Array.isArray(res.dataList) ? res.dataList : [];
          if (append) {
            const existingIds = new Set(archiveLevels.value.map((level) => level.id));
            archiveLevels.value = archiveLevels.value.concat(list.filter((level: LevelItem) => !existingIds.has(level.id)));
          } else {
            archiveLevels.value = list;
          }
          archiveTotal.value = Number(res.total || archiveLevels.value.length || 0);
          archiveHasNext.value = Boolean(res.hasNext);
          archiveNextLastId.value = res.nextLastId || (list.length ? list[list.length - 1].id : null);
          archiveReachedEnd.value = !archiveHasNext.value && archiveLevels.value.length > 0;
          return;
        }
        ElMessage.error(res.msg);
      },
      error: () => {
        if (requestToken !== archiveRequestToken) return;
        archiveLoading.value = false;
        archiveLoadingMore.value = false;
        ElMessage.error('层级档案读取失败，请稍后重试');
      }
    });
  };

  const loadArchiveMore = () => {
    loadArchive(true);
  };

  const openLevel = (level: LevelItem) => {
    if (Number(level.auditStatus) === 1 && tab.value !== 'mine') {
      ElMessage.warning(getBlockedLevelReason(level));
      return;
    }
    router.push({
      path: `/level/${level.id}`,
      query: { source: tab.value }
    });
  };

  const loadLevelDetail = (level: Partial<LevelItem> & { id: number }, onSuccess: (detail: LevelItem) => void) => {
    ajax({
      url: `level/detail/${level.id}`,
      method: 'GET',
      success: (res: any) => {
        if (res.code === 0) {
          onSuccess(res.data || level);
        } else {
          ElMessage.warning(res.msg || getBlockedLevelReason(level));
        }
      },
      error: () => {
        ElMessage.error('层级资料读取失败，请稍后重试');
      }
    });
  };

  const openMine = () => {
    tab.value = 'mine';
    if (!requireLogin()) return;
    leaveVisitStatsRoute();
    ajax({
      url: 'level/mine',
      method: 'POST',
      success: (res: any) => {
        if (res.code === 0) mineLevels.value = res.dataList || [];
        else ElMessage.error(res.msg);
      }
    });
  };

  const openFavorites = () => {
    tab.value = 'favorite';
    if (!requireLogin()) return;
    leaveVisitStatsRoute();
    ajax({
      url: 'level/favorite/list',
      method: 'POST',
      success: (res: any) => {
        if (res.code === 0) favoriteLevels.value = res.dataList || [];
        else ElMessage.error(res.msg);
      }
    });
  };

  const loadPublicSettings = () => {
    ajax({
      url: 'system-setting/public',
      method: 'GET',
      isLoading: false,
      success: (res: any) => {
        if (res.code === 0 && res.data?.patrolExpireDays) {
          patrolExpireDays.value = Number(res.data.patrolExpireDays) || 5;
        }
      }
    });
  };

  const patchLevelInLists = (level: LevelItem) => {
    const patch = (list: LevelItem[]) => {
      const index = list.findIndex((item) => item.id === level.id);
      if (index >= 0) list[index] = { ...list[index], ...level };
    };
    patch(archiveLevels.value);
    patch(mineLevels.value);
    patch(favoriteLevels.value);
  };

  const patrolLevel = (level: LevelItem) => {
    if (!requireLogin()) return;
    ajax({
      url: `level/guard/${level.id}`,
      method: 'POST',
      success: (res: any) => {
        if (res.code === 0) {
          const data = res.data || level;
          patchLevelInLists(data);
          if (activeLevel.value?.id === data.id) activeLevel.value = { ...activeLevel.value, ...data };
          if (editForm.id === data.id) {
            editForm.guardedAt = data.guardedAt || editForm.guardedAt;
          }
          ElMessage.success(res.msg || '巡逻成功');
        } else {
          ElMessage.error(res.msg);
        }
      }
    });
  };

  const patrolActiveLevel = () => {
    if (activeLevel.value) patrolLevel(activeLevel.value);
  };

  const patrolEditingLevel = () => {
    if (!editForm.id) return;
    patrolLevel(editForm as LevelItem);
  };

  return {
    archiveLoading,
    archiveLoadingMore,
    archiveHasNext,
    archiveReachedEnd,
    archiveTotal,
    loadArchive,
    loadArchiveMore,
    openLevel,
    loadLevelDetail,
    openMine,
    openFavorites,
    loadPublicSettings,
    patrolLevel,
    patrolActiveLevel,
    patrolEditingLevel
  };
};
